import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ChevronDown, ChevronUp, ChevronsUpDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { useClientsSummary, type ClientSummaryRow } from '@/hooks/useDashboard'

const PAGE_SIZE = 25

type SortKey = keyof Pick<
  ClientSummaryRow,
  'client_name' | 'invoices_in_range' | 'pending_review' | 'ready_to_export' | 'last_activity'
>

interface Props {
  days: number
}

export function ClientsSummaryTable({ days }: Props) {
  const { data, isLoading, isError } = useClientsSummary(days)
  const [search, setSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('client_name')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)

  const rows = useMemo(() => {
    let r = data ?? []
    if (search.trim()) {
      const q = search.toLowerCase()
      r = r.filter((row) => row.client_name.toLowerCase().includes(q))
    }
    const sorted = [...r].sort((a, b) => {
      const av = a[sortKey] ?? ''
      const bv = b[sortKey] ?? ''
      if (av === bv) return 0
      const cmp = av > bv ? 1 : -1
      return sortDir === 'asc' ? cmp : -cmp
    })
    return sorted
  }, [data, search, sortKey, sortDir])

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const pageRows = rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)

  const onSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key)
      setSortDir(key === 'client_name' ? 'asc' : 'desc')
    }
  }

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return <ChevronsUpDown className="ml-1 inline h-3 w-3 opacity-40" />
    return sortDir === 'asc' ? (
      <ChevronUp className="ml-1 inline h-3 w-3" />
    ) : (
      <ChevronDown className="ml-1 inline h-3 w-3" />
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <CardTitle className="text-base">Clients Invoices</CardTitle>
            <p className="text-xs text-neutral-500">
              {rows.length} client{rows.length === 1 ? '' : 's'} · last {days} days
            </p>
          </div>
          <Input
            placeholder="Search clients…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
            className="h-9 w-64"
          />
        </div>
      </CardHeader>
      <CardContent className="px-4">
        {isLoading && <div className="mx-4 h-40 animate-pulse rounded bg-neutral-100" />}
        {isError && <div className="px-4 text-sm text-red-600">Could not load clients.</div>}
        {!isLoading && !isError && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer" onClick={() => onSort('client_name')}>
                    Client {sortIcon('client_name')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => onSort('invoices_in_range')}
                  >
                    In range {sortIcon('invoices_in_range')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => onSort('pending_review')}
                  >
                    Pending {sortIcon('pending_review')}
                  </TableHead>
                  <TableHead
                    className="cursor-pointer text-right"
                    onClick={() => onSort('ready_to_export')}
                  >
                    Ready to export {sortIcon('ready_to_export')}
                  </TableHead>
                  <TableHead className="cursor-pointer" onClick={() => onSort('last_activity')}>
                    Last activity {sortIcon('last_activity')}
                  </TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-sm text-neutral-500">
                      {search.trim() ? 'No clients match your search.' : 'No clients yet.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((r) => (
                    <TableRow key={r.client_id}>
                      <TableCell className="font-medium">{r.client_name}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.invoices_in_range}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.pending_review > 0 ? (
                          <span className="text-amber-700">{r.pending_review}</span>
                        ) : (
                          <span className="text-neutral-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {r.ready_to_export > 0 ? (
                          <span className="text-blue-700">{r.ready_to_export}</span>
                        ) : (
                          <span className="text-neutral-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-neutral-600">
                        {r.last_activity
                          ? format(parseISO(r.last_activity), 'd MMM yyyy')
                          : '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/invoices/processing?client_id=${encodeURIComponent(r.client_id)}`}
                          className="text-sm font-medium text-[#0000ff] hover:underline"
                        >
                          Open →
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            {rows.length > PAGE_SIZE && (
              <div className="flex items-center justify-between px-4 py-3 text-sm text-neutral-600">
                <span>
                  Page {safePage} of {totalPages}
                </span>
                <div className="flex gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
